import * as React from "react";
import moment from "../../../../utils/app_moment";
import { Select } from "../Select/Select";
import { Input } from "../Input/Input";
import { DatePicker } from "../DatePicker/DatePicker";

const DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

interface SelectorDefault {
    day?: number;
    fromTime?: string;
    toTime?: string;
    startingDate?: moment.Moment;
    endingDate: moment.Moment | null;
}

interface CustomDateSelectorProps {
    onChange: (from: Date, to: Date, endDate?: Date) => void;
    showEndTime?: boolean;
    defaultDate?: SelectorDefault;
    showStartingDate?: boolean;
    showEndingDate?: boolean;
    departments?: string[];
    viewFrom?: "clinician-availability" | "supervisor-intake-session";
}

export default function CustomDateSelector({
    onChange,
    departments,
    showEndTime = departments?.includes("CHP"),
    defaultDate = { endingDate: null },
    showStartingDate = true,
    showEndingDate = false,
    viewFrom = "clinician-availability",
}: CustomDateSelectorProps) {
    const [selectedDay, setSelectedDay] = React.useState(
        defaultDate.day || moment().day()
    );
    const [selectedFromTime, setSelectedFromTime] = React.useState(
        defaultDate.fromTime || "07:00"
    );
    const [selectedToTime, setSelectedToTime] = React.useState(
        defaultDate.toTime || "08:00"
    );
    const [startingDate, setStartingDate] = React.useState(
        defaultDate.startingDate || moment()
    );
    const [endingDate, setEndingDate] = React.useState<moment.Moment | null>(
        defaultDate.endingDate
    );

    React.useEffect(() => {
        const [fromTimeHr, fromTimeMin] = selectedFromTime.split(":");
        const [toTimeHr, toTimeMin] = selectedToTime.split(":");
        const fromDate = startingDate
            .clone()
            .day(selectedDay)
            .hour(parseInt(fromTimeHr))
            .minute(parseInt(fromTimeMin))
            .second(0)
            .milliseconds(0);
        const durationMinutes = viewFrom === "clinician-availability" ? 60 : 45;
        const toDate = startingDate
            .clone()
            .day(selectedDay)
            .hour(showEndTime ? parseInt(toTimeHr) : parseInt(fromTimeHr))
            .minute(
                showEndTime
                    ? parseInt(toTimeMin)
                    : parseInt(fromTimeMin) + durationMinutes
            )
            .second(0)
            .milliseconds(0);
        if (fromDate.isBefore(startingDate, "day")) {
            fromDate.add(1, "week");
            toDate.add(1, "week");
        }
        onChange(
            fromDate.toDate(),
            toDate.toDate(),
            showEndingDate && endingDate ? endingDate.toDate() : undefined
        );
        // eslint-disable-next-line
    }, [selectedDay, selectedFromTime, selectedToTime, startingDate, endingDate]);

    const dayOptions = DAYS.map((day, idx) => ({
        value: idx,
        label: day.substring(0, 3),
    }));

    return (
        <div className="v2-form">
            <Select
                label="on"
                value={dayOptions.find((option) => option.value === selectedDay)}
                onChange={(option) => {
                    if (option && typeof option === "object" && "value" in option) {
                        setSelectedDay(option.value as number);
                    }
                }}
                options={dayOptions}
                isLongListInDialog
            />
            <Input
                label="from"
                type="time"
                min="07:00"
                max="22:00"
                step={900}
                required
                value={selectedFromTime}
                onChange={(e) => {
                    setSelectedFromTime(e.target.value);
                }}
                hasError={showEndTime && selectedFromTime >= selectedToTime}
                errorText="Start time must be before end time"
            />

            {showEndTime && (
                <Input
                    label="to"
                    type="time"
                    min="07:00"
                    max="22:00"
                    step={900}
                    required
                    value={selectedToTime}
                    onChange={(e) => {
                        setSelectedToTime(e.target.value);
                    }}
                    hasError={selectedFromTime >= selectedToTime}
                    errorText="End time must be after start time"
                />
            )}

            {showStartingDate && (
                <DatePicker
                    label="Start date"
                    selected={startingDate.toDate()}
                    onChange={(date) => {
                        if (date) {
                            setStartingDate(moment(date as Date));
                        }
                    }}
                />
            )}

            {showEndingDate && (
                <DatePicker
                    label="End date"
                    selected={endingDate?.toDate()}
                    minDate={startingDate.toDate()}
                    onChange={(date) => {
                        if (date) {
                            setEndingDate(moment(date as Date));
                        } else {
                            setEndingDate(null);
                        }
                    }}
                />
            )}
        </div>
    );
}
